import React, {FC, createContext, useContext, useState, useEffect} from 'react'
import {WithChildren} from '../helpers'

const I18N_CONFIG_KEY = process.env.REACT_APP_I18N_CONFIG_KEY || 'i18nConfig'

type Props = {
  selectedLang: 'en' | 'id'
  setLanguage: (lang: 'en' | 'id') => void
}
const initialState: Props = {
  selectedLang: 'en',
  setLanguage: () => {},
}

function getConfig(): Props {
  const ls = localStorage.getItem(I18N_CONFIG_KEY)
  if (ls) {
    try {
      const config = JSON.parse(ls) as {selectedLang: 'en' | 'id'}
      return {
        selectedLang: config.selectedLang,
        setLanguage: () => {}, // This will be overridden by the provider
      }
    } catch (er) {
      console.error(er)
    }
  }
  return {
    selectedLang: 'en',
    setLanguage: () => {}, // This will be overridden by the provider
  }
}

// Side effect - now just updates localStorage, no reload
export function setLanguage(lang: string, shouldRedirect: boolean = false) {
  localStorage.setItem(I18N_CONFIG_KEY, JSON.stringify({selectedLang: lang}))

  // Only redirect if explicitly requested (when manually changing language)
  if (shouldRedirect) {
    const currentPath = window.location.pathname
    if (currentPath === '/inpatient' || currentPath === '/outpatient') {
      window.location.href = '/claims'
      return
    }
  }

  // Dispatch the event asynchronously to ensure React components have mounted
  // and event listeners are attached (fixes timing issue during impersonation)
  setTimeout(() => {
    window.dispatchEvent(new CustomEvent('languageChanged', {detail: {selectedLang: lang}}))
  }, 0)
}

const I18nContext = createContext<Props>(initialState)

const useLang = () => {
  return useContext(I18nContext).selectedLang
}

const useSetLanguage = () => {
  return useContext(I18nContext).setLanguage
}

const MetronicI18nProvider: FC<WithChildren> = ({children}) => {
  const [lang, setLang] = useState<Props>(() => getConfig())

  // Listen for language change events
  useEffect(() => {
    const handleLanguageChange = (event: CustomEvent) => {
      setLang((prev) => ({
        ...prev,
        selectedLang: event.detail.selectedLang as 'en' | 'id',
      }))
    }

    window.addEventListener('languageChanged', handleLanguageChange as EventListener)

    return () => {
      window.removeEventListener('languageChanged', handleLanguageChange as EventListener)
    }
  }, [])

  const handleSetLanguage = (newLang: 'en' | 'id') => {
    setLanguage(newLang)
  }

  return (
    <I18nContext.Provider
      value={{
        selectedLang: lang.selectedLang,
        setLanguage: handleSetLanguage,
      }}
    >
      {children}
    </I18nContext.Provider>
  )
}

export {MetronicI18nProvider, useLang, useSetLanguage}
